/**
 * MNO-IP-Range API-Naht — echte Facade-Integration über den BFF (Spec 326).
 *
 * Löst das Scaffold-Mock (#318) ab: list/get/create/update/delete proxyen über
 * den Admin-BFF (`/v1/admin/mno-ip-ranges`), der `X-Admin-Key` serverseitig setzt.
 * Composite Key = operatorId + countryCode. Der BFF liefert/erwartet camelCase.
 */
import { get, post, put, del } from './client';
import type {
  CreateMnoIpRangeInput,
  MnoIpRangeEntry,
  MnoIpRangeStatus,
  MnoOperator,
  UpdateMnoIpRangeInput,
} from '@/types/mno-ip-range';

const BASE = '/admin/mno-ip-ranges';

function resourcePath(operatorId: string, countryCode: string): string {
  return `${BASE}/${encodeURIComponent(operatorId)}/${encodeURIComponent(countryCode)}`;
}

export interface MnoIpRangeFilters {
  country?: string;
  operatorId?: string;
  status?: MnoIpRangeStatus;
}

interface MnoIpRangeListResponse {
  entries: MnoIpRangeEntry[];
  total: number;
}

export async function listMnoIpRanges(filters?: MnoIpRangeFilters): Promise<MnoIpRangeEntry[]> {
  const params: Record<string, string> = {};
  if (filters?.country) params.country = filters.country;
  if (filters?.operatorId) params.operatorId = filters.operatorId;
  if (filters?.status) params.status = filters.status;
  const res = await get<MnoIpRangeListResponse>(
    BASE,
    Object.keys(params).length > 0 ? params : undefined,
  );
  return res.entries;
}

export async function getMnoIpRange(
  operatorId: string,
  countryCode: string,
): Promise<MnoIpRangeEntry> {
  return get<MnoIpRangeEntry>(resourcePath(operatorId, countryCode));
}

export async function createMnoIpRange(input: CreateMnoIpRangeInput): Promise<MnoIpRangeEntry> {
  return post<MnoIpRangeEntry>(BASE, input);
}

export async function updateMnoIpRange(
  operatorId: string,
  countryCode: string,
  input: UpdateMnoIpRangeInput,
): Promise<MnoIpRangeEntry> {
  return put<MnoIpRangeEntry>(resourcePath(operatorId, countryCode), input);
}

export async function deleteMnoIpRange(operatorId: string, countryCode: string): Promise<void> {
  await del<void>(resourcePath(operatorId, countryCode));
}

/**
 * Operator-Quelle = Contract-Enum (Spec 326 §3): die drei deutschen MNOs.
 * Statische Liste, da die Facade-Admin-API keinen Operator-Listen-Endpoint für
 * IP-Ranges bietet. Alle in DE — die länderabhängige Auswahl (#322) bleibt.
 */
const OPERATORS: readonly MnoOperator[] = [
  { operatorId: 'D1_DE', name: 'Telekom Deutschland', country: 'DE' },
  { operatorId: 'D2_DE', name: 'Vodafone Deutschland', country: 'DE' },
  { operatorId: 'O2_DE', name: 'Telefónica (O2) Deutschland', country: 'DE' },
] as const;

export async function listMnoOperators(): Promise<MnoOperator[]> {
  return OPERATORS.map((op) => ({ ...op }));
}
